import axios from 'axios';
import {nextTick, ref} from 'vue';
import {useRoute, useRouter} from 'vue-router';

import {AuthApi} from '@/shared/features/auth/auth.api';
import type {AuthToken, UserWithToken} from '@/shared/features/auth/auth.model';
import type {SsoLoginButton} from '@/shared/features/ssoConnections/ssoConnection.model';
import {useDashboardStore} from '@/shared/stores/dashboard.store';
import {useUserStore} from '@/shared/stores/user.store';
import {useSwalMessage} from '@/shared/utils/showSwalMessage';

interface ValidationErrors {
   [key: string]: string | {message: string};
}

/**
 * The full-page loader, shared by every caller of this composable.
 *
 * Module scope on purpose. Declared inside the function each component got its own copy, so the
 * sign-in buttons raised a flag the page they sit on was not watching: pressing one appeared to
 * do nothing for the second or two it takes to ask the server where to send the browser. There
 * is one page and one loader, so there is one piece of state.
 */
const showLoader = ref<boolean>(true);

export default function useAuth() {
   const showSwalMessage = useSwalMessage();
   const userStore = useUserStore();
   const {setUser, clearUser, setToken, deleteToken} = userStore;

   const dashboardDataStore = useDashboardStore();
   const processing = ref<boolean>(false);
   const validationErrors = ref<ValidationErrors>({});
   const router = useRouter();
   const route = useRoute();
   const successMessage = ref<string | null>(null);

   const handleRequest = async <T>(
      url: string,
      payload: T | object = {},
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onSuccess: (response: any) => void,
      onError: ((error: unknown) => void) | null = null,
      token: string | AuthToken | null = null,
      method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'POST',
      headers: {[key: string]: string} = {},
      hideLoaderOnSuccess: boolean = true,
   ) => {
      processing.value = true;
      validationErrors.value = {};
      showLoader.value = true;

      try {
         let defaultHeaders = {};
         if(token) {
            if(typeof token === 'string') {
               defaultHeaders = {Authorization: `Bearer ${token}`};
            } else {
               defaultHeaders = {Authorization: `Bearer ${token.access_token}`};
            }
         }
         const mergedHeaders = {...defaultHeaders, ...headers};

         // eslint-disable-next-line @typescript-eslint/no-explicit-any
         let response: any;

         if (method === 'GET') {
            response = await AuthApi.get<T>(url, {
               headers: mergedHeaders,
               params: payload,
            });
         } else {
            response = await AuthApi.request<T>({
               url,
               method,
               data: payload,
               headers: mergedHeaders,
            });
         }

         showLoader.value = !hideLoaderOnSuccess;
         onSuccess(response);
      } catch (error) {
         showLoader.value = false;
         if (onError) {
            onError(error);
         } else {
            handleError(error);
         }
      } finally {
         processing.value = false;
      }
   };

   const handleError = (error: unknown) => {
      if (axios.isAxiosError(error)) {
         if (error.response?.data?.errors) {
            validationErrors.value = error.response.data.errors;
         } else {
            validationErrors.value = {
               custom: {
                  message: error.response?.data?.message ?? 'Undefined error',
               },
            };
         }
      } else {
         validationErrors.value = {
            custom: {
               message: 'Undefined error',
            },
         };
      }
   };

   const submitLogin = async <T>(payload: T): Promise<void> => {
      if (processing.value) return;

      successMessage.value = null;

      await handleRequest(
         '/auth/login',
         payload,
         async (response) => {
            if (response.action === 'auth-token') {
               showLoader.value = true;
               setUser(response.user);
               setToken(response.token);
               await nextTick();
               if (response.home !== 'student-center') {
                  await dashboardDataStore.loadFreshData();
               }
               await router.push({name: `${response.home}.index`});
            } else {
               successMessage.value = response.message;
            }
         },
      );
   };

   const submitStudentLogin = async <T>(payload: T): Promise<void> => {
      if (processing.value) return;

      successMessage.value = null;

      await handleRequest(
         '/auth/student-login',
         payload,
         async (response) => {
            if (response.action === 'auth-token') {
               showLoader.value = true;
               setUser(response.user);
               setToken(response.token);
               await router.push({name: `${response.home}.index`});
            } else {
               successMessage.value = response.message;
            }
         },
      );
   };

   /**
    * Begin a sign-in through one of the tenant's configured connections.
    *
    * Asks the backend where to send the browser rather than building the URL here: the
    * authorization request carries a state and a nonce the server has to remember, so only the
    * server can compose it. Works for any provider and any protocol — the button only knows the
    * connection's uuid.
    *
    * @param button The button the person pressed, from the tenant config store.
    * @param returnRoute An internal route name to land on afterwards, for a deep link that
    *                    bounced somebody to the login page. Ignored by the server unless it
    *                    names a route it recognises, so it can never become an open redirect.
    */
   const ssoLogin = async (button: SsoLoginButton, returnRoute?: string): Promise<void> => {
      if (processing.value) return;

      // Before anything else, so the page answers the press rather than the request. Asking the
      // server where to send the browser takes a moment on a cold connection, and until this the
      // only sign anything had happened was the button fading slightly.
      showLoader.value = true;

      // Absolute, against the page's own origin, because this one endpoint does not live under
      // /api. It sits at /sso, where an administrator pastes it into a provider's console and a
      // district puts it behind a portal tile, and the axios instance would otherwise prefix it
      // with the API base and ask for /api/sso/… , which is not a route and answers 404.
      const start = `${window.location.origin}${button.start_url}`;

      const url = returnRoute
         ? `${start}?return=${encodeURIComponent(returnRoute)}`
         : start;

      await handleRequest(
         url,
         {},
         async (response) => {
            // Left up: the browser is leaving for the provider, and taking it down first would
            // show the login page again for the moment before it goes.
            showLoader.value = true;
            window.location.href = response.data.url;
         },
         // Down again on a refusal, with the reason shown. handleRequest already lowers it, and
         // this keeps the message going to the same place every other failure here does.
         handleError,
         null,
         'GET',
         {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            Expires: '0',
         },
         false,
      );
   };

   const submitRegister = async <T>(payload: T): Promise<void> => {
      if (processing.value) return;

      await handleRequest(
         '/register',
         payload,
         () => {
            showSwalMessage('success', 'Registration successfully');
            router.push({name: 'auth.login'});
         },
         handleError,
      );
   };

   const submitForgotPassword = async <T>(payload: T): Promise<void> => {
      if (processing.value) return;

      await handleRequest(
         '/forget-password',
         payload,
          
         (_r) => {
            showSwalMessage(
               'success',
               'We have emailed your password reset link! Please check your mail inbox.',
            );
         },
         handleError,
      );
   };

   const submitResetPassword = async <T>(payload: T): Promise<void> => {
      if (processing.value) return;

      await handleRequest(
         '/reset-password',
         payload,
         async () => {
            showSwalMessage('success', 'Password successfully changed.');
            await router.push({name: 'auth.login'});
         },
         handleError,
      );
   };

   const checkUser = async () => {
      if (!userStore.token) {
         showLoader.value = false;
         return;
      }
      await handleRequest(
         '/user',
         {},
         async (response) => {
            if (response) {
               setUser(response);
            } else {
               successMessage.value = response.message;
            }
         },
         deleteToken,
         null,
         'GET',
      );
   };

   /**
    * Sign out.
    *
    * When the person arrived through an SSO connection that ends the provider's session too, the
    * server answers with a URL and the browser is sent there instead of to the landing page —
    * after this side has been cleared, so leaving does not strand a half-signed-out app. The
    * provider then returns them to /login itself.
    *
    * @param noQuery Skip the API call, for a session already known to be gone (an expired token).
    */
   const logout = async (noQuery?: boolean): Promise<void> => {
      if (processing.value) return;

      processing.value = true;

      try {
         let providerSignOut: string | null = null;

         if(noQuery !== true) {
            providerSignOut = (await AuthApi.logout())?.sso_logout_url ?? null;
         }
         clearUser();
         dashboardDataStore.resetData();

         if(providerSignOut) {
            window.location.href = providerSignOut;
         } else if(noQuery) {
            window.location.href = '/';
         } else {
            await router.push('/');
         }

      } catch (_error) {
         showSwalMessage('error', 'Error logging out');
      } finally {
         processing.value = false;
      }
   };

   const logInByTokenLink = async () => {
      if (route.name === 'landing.ssoLogin') {
         return;
      }
      const tokenFromQuery = route.query['auth-key'] as string | undefined;

      if (!tokenFromQuery) {
         showLoader.value = false;
         return;
      }

      if (processing.value) return;

      const authToken: Record<string, string> = {
         'auth-key': tokenFromQuery,
      };

      await handleRequest(
         '/auth/key',
         authToken,
         async (response: UserWithToken) => {
            if (response.action === 'auth-token') {
               setUser(response.user);
               setToken(response.token);
               showLoader.value = true;
               if (response.home === 'student-center') {
                  await router.push({name: 'student-center.index'});
               } else {
                  await dashboardDataStore.loadFreshData();
                  await router.push({name: 'dashboard.index'});
               }
            } else {
               successMessage.value = response.message;
            }
         },
      );
   };

   /**
    * Finish a sign-in the identity provider has just sent the browser back from.
    *
    * Runs on the `/login/sso/:uuid` page, which is the redirect URI registered with the
    * provider. The uuid in the address says which connection this is, so one page serves every
    * provider a tenant has configured.
    *
    * A refusal comes back as a plain sentence with no detail about which check failed — that is
    * deliberate on the server, and repeating it here would undo it. The administrator sees the
    * real reason on the connection's activity page.
    */
   const completeSsoLogin = async () => {
      if (route.name !== 'landing.ssoLogin') {
         return;
      }

      const uuid = route.params.uuid as string | undefined;
      const code = route.query['code'] as string | undefined;
      const state = route.query['state'] as string | undefined;

      if (!uuid || !code || !state) {
         showLoader.value = false;
         validationErrors.value = {
            custom: {
               message: 'This sign-in did not complete. Please try again.',
            },
         };
         return;
      }

      if (processing.value) return;

      // Up before the request rather than inside it. The page that mounts this lowers the loader
      // on the way in, not knowing a sign-in is about to be finished, and relying on the two
      // landing in the same tick is a quiet dependency on ordering.
      showLoader.value = true;

      await handleRequest(
         `/auth/sso/${uuid}/callback`,
         {code, state},
         async (response) => {
            if (response.action === 'auth-sso') {
               setUser(response.user);
               setToken(response.token);

               // Staff only, the same guard every other sign-in path uses. A student has no
               // access to the dashboard's data endpoint, so asking for it on their behalf
               // answers 403 — and the throw took the navigation below with it, leaving somebody
               // who had signed in perfectly well sitting on the callback page.
               if (response.home !== 'student-center') {
                  await dashboardDataStore.initData(true);
               }

               // The route the person was heading for before they were bounced to sign in. The
               // server only echoes one it recognises, so it is safe to navigate to.
               await router.push({name: response.return ?? `${response.home}.index`});
            } else if (response.action === 'sso-test-complete') {
               // A wizard test. Nobody is signed in and nothing was written, so there is nowhere
               // to navigate to — the window says it is finished and the page that opened it,
               // which is polling for the result, shows what the test found. Nothing follows, so
               // the loader comes down to let the message be read.
               showLoader.value = false;
               successMessage.value = 'Test sign-in complete. You can close this window and return'
                  + ' to the setup page for the result.';
            } else {
               showLoader.value = false;
               successMessage.value = response.message;
            }
         },
         handleError,
         null,
         'POST',
         {},
         // The loader stays up through this one. Coming back from the provider is not the end of
         // the work: the account still has to be stored, the dashboard's data fetched and the
         // route pushed, and that is a second or two. Lowering it when the request returned —
         // which is what the default does — put the login page back on screen for exactly that
         // long, so somebody who had just signed in was shown the form asking them to sign in.
         false,
      );
   };

   const hideLoader = async () => {
      showLoader.value = false;
   };

   return {
      validationErrors,
      processing,
      submitLogin,
      submitStudentLogin,
      submitRegister,
      submitForgotPassword,
      submitResetPassword,
      successMessage,
      showLoader,
      hideLoader,
      checkUser,
      logout,
      logInByTokenLink,
      ssoLogin,
      completeSsoLogin,
   };
}
